import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { createObservation } from "../../services/observationService";
import { getActiveRubric } from "../../services/rubricService";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import useDialog from "../../utils/hooks/useDialog";
import useAuth from "../../utils/hooks/useAuth";
import useOrgScope from "../../utils/hooks/useOrgScope";
import usePagedUsers from "../../utils/hooks/usePagedUsers";
import { slotErrorMessage } from "../../utils/helpers/slotErrors";
import {
  OBSERVATION_STATUS,
  OBSERVATION_TYPE,
} from "../../utils/constants/observations";
import { fullName } from "../../utils/helpers/userDisplay";
import Avatar from "../Avatar/Avatar";
import { formatShortDate, toLocalISODate } from "../../utils/helpers/dateDisplay";
import Spinner from "../Spinner/Spinner";
import PeriodPicker, { lessonFields } from "./PeriodPicker";
import FocusStandardPicker, { shownFocus } from "./FocusStandardPicker";

// The foot of a paged picker list: how much of the roster is on screen, and
// the next page. Rendered only while there IS a next page, so a school that
// fits in one never sees it.
function PickerMore({ list }) {
  const { t } = useTranslation();
  if (!list.hasMore) return null;
  const failed = list.status === "moreFailed";
  return (
    <div className="pt-1 pb-2 flex flex-col items-center gap-2">
      <p className="text-[11px] font-bold text-slate-400 text-center">
        {failed
          ? t("schedule.pickerLoadFailed")
          : t("schedule.pickerShownOf", { shown: list.users.length, total: list.count })}
      </p>
      <button
        type="button"
        onClick={list.loadMore}
        disabled={list.status === "loadingMore"}
        className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 disabled:opacity-60"
      >
        {list.status === "loadingMore"
          ? t("common.loading")
          : failed
            ? t("common.retry")
            : t("schedule.pickerLoadMore")}
      </button>
    </div>
  );
}

// A first page that failed, said as such: an empty list here would read as
// "nobody at this school", which is the one wrong conclusion to hand a manager.
function PickerError({ list }) {
  const { t } = useTranslation();
  return (
    <div className="py-6 flex flex-col items-center gap-2">
      <p className="text-xs font-bold text-rose-600">{t("schedule.pickerLoadFailed")}</p>
      <button
        type="button"
        onClick={list.retry}
        className="text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-800"
      >
        {t("common.retry")}
      </button>
    </div>
  );
}

// Manager-facing: schedule a future OFFICIAL observation. Lands status=SCHEDULED
// (not draft) so it shows on the teacher's Upcoming tab + fires a notification.
//
// `mainObserver` / `pickObserver` are both OPTIONAL and the modal is exactly
// what it was without them — the dashboard's button passes neither. Given
// either, the visit is booked in that person's name: the API has accepted and
// permission-checked `main_observer` all along, this modal simply never sent it,
// which is why a visit a manager schedules for an evaluator has until now been
// credited to the manager who booked it rather than to whoever walks into the
// lesson. `pickObserver` makes that person choosable, from the server's own
// list of who may conduct (`?can_conduct=true`) — never from a caller's rows,
// which on the coverage page were one table page of twenty.
export default function ScheduleObservationModal({
  onClose,
  onSubmitted,
  mainObserver,
  pickObserver = false,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const todayISO = toLocalISODate(new Date());
  const [rubric, setRubric] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 300);
  const [observerQuery, setObserverQuery] = useState("");
  const debouncedObserverQuery = useDebouncedValue(observerQuery, 300);
  const [date, setDate] = useState(todayISO);
  const [period, setPeriod] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [focus, setFocus] = useState([]);
  const [note, setNote] = useState("");
  // null means "send no main_observer", which is the server's own default of
  // the caller — not a missing value to validate. Held as the person, not a
  // uuid: the one picked may be on a page of the list that is no longer loaded.
  const [observer, setObserver] = useState(mainObserver || null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Escape, focus trap, scroll lock, focus restore. This was the one
  // modal in the app without it.
  const dialogRef = useDialog(busy ? () => {} : onClose);

  // The rubric of the TEACHER's school, not the caller's: a head-office
  // observer's own default can differ from the school the visit is filed on,
  // and the focus picked here must be standards of the rubric it is scored on.
  const teacherSchoolUuid = teacher?.organization?.uuid;
  useEffect(() => {
    const controller = new AbortController();
    getActiveRubric({
      params: { organization: teacherSchoolUuid || undefined },
      signal: controller.signal,
    })
      .then(setRubric)
      .catch(() => setRubric(null));
    return () => controller.abort();
  }, [teacherSchoolUuid]);

  // The school picked in the app header narrows the roster, as on the
  // observation form; no pick means everyone in scope.
  const { pickedOrgUuid, pickPending } = useOrgScope();
  const organization = pickedOrgUuid || undefined;

  // Both lists are paged and searched on the server, so a school of any size
  // costs one page up front; the rest is «show more» or a narrower search.
  const teacherList = usePagedUsers(
    { observable: "true", search: debounced || undefined, organization },
    { enabled: !pickPending },
  );
  const observerList = usePagedUsers(
    { can_conduct: "true", search: debouncedObserverQuery || undefined, organization },
    { enabled: pickObserver && !pickPending },
  );

  const chooseObserver = (person) => {
    setObserver(person);
    setObserverQuery("");
  };

  const resetSlot = () => {
    setPeriod(null);
    setLesson(null);
  };

  const submit = async () => {
    if (!teacher || !date || !period) return;
    const focusSent = shownFocus(rubric, focus, lesson?.subject?.uuid);
    setError("");
    setBusy(true);
    try {
      await createObservation({
        body: {
          observed_teacher: teacher.uuid,
          template_version: rubric?.uuid,
          type: OBSERVATION_TYPE.OFFICIAL,
          status: OBSERVATION_STATUS.SCHEDULED,
          scheduled_date: date,
          period,
          ...lessonFields(lesson),
          ...(observer ? { main_observer: observer.uuid } : {}),
          ...(focusSent.length ? { focus_standards: focusSent } : {}),
          // Scheduling note is the observer's focus note, not the lesson topic.
          general_comment: note,
        },
      });
      toast.success(t("schedule.scheduledToast"));
      onSubmitted?.();
    } catch (err) {
      setError(slotErrorMessage(err, t, t("upcoming.requestFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-observation-title"
        tabIndex={-1}
        className="relative bg-white rounded-[36px] shadow-2xl w-full max-w-xl overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <h3 id="schedule-observation-title" className="text-lg font-black text-slate-900">
            {t("schedule.scheduleObservationTitle")}
          </h3>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400"
            aria-label={t("common.close")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {!teacher ? (
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                {t("upcoming.selectTeacher")}
              </label>
              <input
                type="text"
                placeholder={t("upcoming.searchByName")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
              <div className="space-y-2 max-h-[260px] overflow-y-auto">
                {teacherList.status === "loading" ? (
                  <Spinner label={t("upcoming.loadingTeachers")} size="sm" className="py-8" />
                ) : teacherList.status === "error" ? (
                  <PickerError list={teacherList} />
                ) : teacherList.users.length > 0 ? (
                  <>
                    {teacherList.users.map((u) => (
                      <button
                        key={u.uuid}
                        onClick={() => {
                          setTeacher(u);
                          setQuery("");
                          resetSlot();
                        }}
                        className="w-full p-3 rounded-2xl border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-all flex items-center gap-3 text-left"
                      >
                        <Avatar
                          user={u}
                          className="w-9 h-9 rounded-xl bg-indigo-100 text-indigo-600 font-black shrink-0"
                        />
                        <span className="text-xs font-black text-slate-700 truncate">{fullName(u)}</span>
                      </button>
                    ))}
                    <PickerMore list={teacherList} />
                  </>
                ) : (
                  <p className="py-8 text-center text-xs text-slate-400 font-bold">
                    {t("upcoming.noTeachersFound")}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Avatar
                    user={teacher}
                    className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl font-black"
                  />
                  <span className="text-sm font-black text-slate-800">{fullName(teacher)}</span>
                </div>
                <button
                  onClick={() => {
                    setTeacher(null);
                    resetSlot();
                  }}
                  className="text-[9px] font-black text-indigo-500 uppercase tracking-widest hover:text-indigo-700"
                >
                  {t("upcoming.changeTeacher")}
                </button>
              </div>

              {/* Who CONDUCTS, right under who is observed — the two halves of
                  the sentence the visit is. Rendered only when the caller named
                  someone or asked for the picker, so the dashboard's own button
                  keeps a modal with one person in it. */}
              {(mainObserver || pickObserver) &&
                (pickObserver ? (
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                      {t("schedule.mainObserverLabel")}
                    </p>
                    {observer ? (
                      <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar
                            user={observer}
                            className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl font-black shrink-0"
                          />
                          <span className="text-sm font-black text-slate-800 truncate">
                            {fullName(observer)}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => chooseObserver(null)}
                          className="text-[9px] font-black text-indigo-500 uppercase tracking-widest hover:text-indigo-700 shrink-0"
                        >
                          {t("schedule.changeObserver")}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder={t("schedule.searchObserver")}
                          value={observerQuery}
                          onChange={(e) => setObserverQuery(e.target.value)}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                        />
                        <div className="space-y-2 max-h-[260px] overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => chooseObserver(null)}
                            className="w-full p-3 rounded-2xl border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-all flex items-center gap-3 text-left"
                          >
                            <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 shrink-0 flex items-center justify-center">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </span>
                            <span className="text-xs font-black text-slate-700 truncate">
                              {t("schedule.mainObserverSelf")}
                            </span>
                          </button>
                          {observerList.status === "loading" ? (
                            <Spinner label={t("common.loading")} size="sm" className="py-6" />
                          ) : observerList.status === "error" ? (
                            <PickerError list={observerList} />
                          ) : observerList.users.length > 0 ? (
                            <>
                              {observerList.users.map((o) => (
                                <button
                                  key={o.uuid}
                                  type="button"
                                  onClick={() => chooseObserver(o)}
                                  className="w-full p-3 rounded-2xl border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-all flex items-center gap-3 text-left"
                                >
                                  <Avatar
                                    user={o}
                                    className="w-9 h-9 rounded-xl bg-indigo-100 text-indigo-600 font-black shrink-0"
                                  />
                                  <span className="text-xs font-black text-slate-700 truncate">
                                    {fullName(o)}
                                  </span>
                                </button>
                              ))}
                              <PickerMore list={observerList} />
                            </>
                          ) : (
                            <p className="py-6 text-center text-xs text-slate-400 font-bold">
                              {t("schedule.noObserversFound")}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                      {t("schedule.mainObserverLabel")}
                    </p>
                    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <Avatar
                        user={mainObserver}
                        className="w-9 h-9 rounded-xl bg-slate-200 text-slate-600 font-black shrink-0"
                      />
                      <span className="text-sm font-black text-slate-800 truncate">
                        {fullName(mainObserver)}
                      </span>
                    </div>
                  </div>
                ))}

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                  {t("upcoming.visitDate")}
                </label>
                <input
                  type="date"
                  value={date}
                  min={todayISO}
                  onChange={(e) => {
                    setDate(e.target.value);
                    resetSlot();
                  }}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                  {t("upcoming.pickLessonLabel")}
                </label>
                <PeriodPicker
                  teacherUuid={teacher.uuid}
                  date={date}
                  value={period}
                  observerUuid={observer?.uuid || user?.uuid}
                  onChange={(p, l) => {
                    setPeriod(p);
                    setLesson(l);
                  }}
                />
              </div>

              {(rubric?.standards || []).length > 0 && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                    {t("upcoming.focusLabel")}
                  </label>
                  <FocusStandardPicker
                    rubric={rubric}
                    subject={lesson?.subject?.uuid}
                    value={focus}
                    onChange={setFocus}
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                  {t("upcoming.addNote")}
                </label>
                <textarea
                  placeholder={t("upcoming.notePlaceholder")}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 min-h-[70px] resize-none"
                />
              </div>

              {date && period && (
                <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl">
                  <p className="text-[11px] font-bold text-indigo-800 leading-relaxed">
                    {t(lesson?.group?.name ? "schedule.reviewLineGroup" : "schedule.reviewLineNoGroup", {
                      observer: fullName(observer || user),
                      name: fullName(teacher),
                      group: lesson?.group?.name,
                      date: formatShortDate(date),
                      period,
                    })}
                  </p>
                </div>
              )}

              {error && (
                <p className="text-[11px] font-black text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">
                  {error}
                </p>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={onClose}
                  disabled={busy}
                  className="flex-1 py-3.5 bg-slate-100 text-slate-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 disabled:opacity-60"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={submit}
                  disabled={!date || !period || busy}
                  className={`flex-1 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                    !date || !period || busy
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200"
                  }`}
                >
                  {busy ? t("upcoming.sending") : t("schedule.scheduleObservation")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
